import { styled, useTheme } from '@mui/material';
import { Typography } from '../../atoms/Typography/Typography';
import remove from '../../../assets/icons/remove.svg';
import { ReactComponent as InfoErrorIcon } from '../../../assets/icons/error-icon-banner.svg';
import { ReactComponent as SuccessTickIcon } from '../../../assets/icons/success_tick.svg';
import { ReactComponent as InfoAlertIcon } from '../../../assets/icons/info-alert.svg';
import { ReactComponent as AnnouncementIcon } from '../../../assets/icons/announcement.svg';
import { ReactComponent as AddIcon } from '../../../assets/icons/add.svg';
import { Icon } from '../../atoms';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '../../atoms';
import { useNavigate } from 'react-router-dom';
import { ADVISOR_ROUTES } from '../../../routes/routeConstants';

export interface BannerProps {
    type: 'success' | 'attention' | 'alert' | 'announcement' | 'warning' | 'error';
    disabled?: boolean;
    isTimer?: boolean;
    message?: string;
    messageNode?: React.ReactNode;
    handleClose?: () => void;
    handleTryAgain?: () => void;
    hyperlinkLabelText?: string;
}

interface DivTypeProps {
    background: string;
    border: string;
    icon: JSX.Element;
    message: string;
    messagePrefix: string;
    hyperlinkLabel: string;
    hyperlinkIcon?: JSX.Element;
}

export interface MainDivProps {
    type: BannerProps['type'];
    disabled: boolean;
}

export interface DisabledProps {
    disabled: boolean;
}

const StyledContentDiv = styled('div')({
    display: 'flex',
    alignItems: 'center',
    width: '100%'
});

const StyledCloseDiv = styled('div')<{ type: BannerProps['type'] }>(({ type }) => ({
    display: 'flex',
    alignItems: type === 'announcement' ? 'flex-start' : 'center',
    cursor: 'pointer',
    justifyContent: 'flex-start'
}));

const StyledIconDiv = styled('div')({
    display: 'flex',
    width: '32px',
    margin: '1px 2.6px 1.2px 2px',
    justifyContent: 'center',
    alignItems: 'center'
});

const StyledContent = styled(Typography)(({ theme }) => ({
    fontSize: theme.spacing(2),
    margin: 0,
    fontWeight: 400,
    lineHeight: '22px',
    color: theme.palette.semantic.text.default
}));

const StyledTryAgain = styled('div')(({ theme }) => ({
    display: 'flex',
    cursor: 'pointer',
    marginLeft: theme.spacing(2),
    img: {
        width: 18,
        marginBottom: theme.spacing(0.3)
    }
}));

type StyledTryAgainTextProps = DisabledProps;
const StyledTryAgainText = styled(Typography)<StyledTryAgainTextProps>(({ disabled, theme }) => ({
    fontSize: theme.spacing(2),
    marginLeft: theme.spacing(1),
    fontWeight: 600,
    color: `${disabled ? theme.palette.semantic.int.disabled2 : theme.palette.semantic.int.link}`,
    borderBottom: `1.5px solid ${disabled ? theme.palette.semantic.int.disabled2 : theme.palette.semantic.int.link}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
    minWidth: theme.spacing(12),
    lineHeight: theme.spacing(2.5)
}));

const StyledCloseImg = styled('img')<{ type: BannerProps['type'] }>(({ type }) => ({
    width: type === 'announcement' ? '18px' : '24px',
    height: type === 'announcement' ? '18px' : '24px'
}));

export const Banner = ({
    type,
    handleClose,
    handleTryAgain,
    disabled = false,
    message,
    messageNode,
    hyperlinkLabelText
}: BannerProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const divTypeMapper: Record<BannerProps['type'], DivTypeProps> = {
        success: {
            background: theme.palette.semantic.bg.success1,
            border: theme.palette.semantic.bd.success,
            icon: <SuccessTickIcon />,
            message: t('BANNER_SUCCESS_MESSAGE'),
            messagePrefix: '',
            hyperlinkLabel: ''
        },
        attention: {
            background: theme.palette.semantic.bg.error,
            border: theme.palette.semantic.bd.error,
            icon: <InfoErrorIcon />,
            message: message ? message : t('FS_ERROR_BANNER_MSG'),
            messagePrefix: t('NEEDS_ATTENTION'),
            hyperlinkLabel: hyperlinkLabelText ? hyperlinkLabelText : t('BANNER_ATTENTION_LINK_TEXT')
        },
        alert: {
            background: theme.palette.semantic.bg.secondary1,
            border: theme.palette.semantic.bd.secondary,
            icon: <InfoAlertIcon />,
            message: message || t('FS_ALERT_BANNER_MSG'),
            messagePrefix: t('ALERT'),
            hyperlinkLabel: t('ADD_FUNDING'),
            hyperlinkIcon: <AddIcon />
        },
        announcement: {
            background: theme.palette.semantic.bg.default,
            border: theme.palette.semantic.bd.brand,
            icon: <AnnouncementIcon />,
            message: message || t('FS_ANNOUNCEMENT_BANNER_MSG'),
            messagePrefix: t('FS_ANNOUNCEMENT_BANNER_HEADING'),
            hyperlinkLabel: t('FS_ANNOUNCEMENT_BANNER_HYPERLINK_LABEL')
        },
        warning: {
            background: theme.palette.semantic.bg.warning1,
            border: theme.palette.semantic.bd.warning,
            icon: <Icon id="warning-triangle" size={20} />,
            message: message || '',
            messagePrefix: t('ATTENTION_NEEDED'),
            hyperlinkLabel: hyperlinkLabelText || ''
        },
        error: {
            background: theme.palette.semantic.bg.error,
            border: theme.palette.semantic.bd.error,
            icon: <InfoErrorIcon />,
            message: message || '',
            messagePrefix: '',
            hyperlinkLabel: ''
        }
    };
    const StyledMainDiv = styled('div')<MainDivProps>(({ disabled, theme, type }) => ({
        display: 'flex',
        padding: type === 'announcement' ? theme.spacing(2) : `${theme.spacing(1)} ${theme.spacing(2)}`,
        gap: theme.spacing(2),
        margin: type === 'announcement' ? `${theme.spacing(2.5)} ${theme.spacing(2.5)} 0 ${theme.spacing(2.5)}` : 0,
        borderRadius: '6px',
        background: disabled ? theme.palette.neutral[50] : divTypeMapper[type].background,
        border: disabled
            ? 'none'
            : type === 'announcement'
            ? `2px solid ${divTypeMapper[type].border}`
            : `1px solid ${divTypeMapper[type].border}`,
        boxShadow: '0 4px 10px 0 rgba(0, 0, 0, 0.05)',
        left: 0,
        bottom: 0,
        zIndex: 99
    }));
    const routeToRecentUpdatesPage = () => {
        navigate(ADVISOR_ROUTES.RECENT_UPDATES);
    };
    return (
        <StyledMainDiv type={type} disabled={disabled}>
            <StyledIconDiv>{divTypeMapper[type].icon}</StyledIconDiv>
            <StyledContentDiv>
                {type === 'announcement' ? (
                    <StyledContent>
                        <Typography variant="body.large" mb={8}>
                            {divTypeMapper[type].messagePrefix}
                        </Typography>
                        <Typography variant="body.small">
                            {`${divTypeMapper[type].message}`}
                            <BasicButton
                                sx={{ fontSize: '13px', marginBottom: '4px' }}
                                variant="text"
                                size="small"
                                onClick={routeToRecentUpdatesPage}
                            >
                                {divTypeMapper[type].hyperlinkLabel}
                            </BasicButton>
                        </Typography>
                    </StyledContent>
                ) : (
                    <StyledContent>
                        {divTypeMapper[type].messagePrefix && <b>{divTypeMapper[type].messagePrefix}</b>}
                        {messageNode ?? divTypeMapper[type].message}
                    </StyledContent>
                )}
                {handleTryAgain && (
                    <StyledTryAgain
                        onClick={handleTryAgain}
                        style={type === 'warning' ? { marginLeft: 'auto' } : undefined}
                    >
                        {divTypeMapper[type].hyperlinkIcon}
                        <StyledTryAgainText
                            label={divTypeMapper[type].hyperlinkLabel}
                            disabled={disabled}
                            theme={theme}
                        />
                    </StyledTryAgain>
                )}
            </StyledContentDiv>
            {handleClose && (
                <StyledCloseDiv type={type}>
                    <StyledCloseImg type={type} src={remove} onClick={handleClose} />
                </StyledCloseDiv>
            )}
        </StyledMainDiv>
    );
};
